import React from 'react';
import { Box } from '@mui/material';
import {
  CallSplit as SplitIcon,
  Check as CheckIcon,
  LockOutlined as LockIcon,
  PersonOutline as PersonIcon,
  SwapHoriz as SettleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Container, IconTile, Initial, Section, TelegramCta } from './primitives';
import { formatBRL, richText } from './richText';
import { landing, people } from './tokens';

const features = [
  { key: 'split', icon: <SplitIcon /> },
  { key: 'settle', icon: <SettleIcon /> },
  { key: 'share', icon: <PersonIcon /> },
  { key: 'private', icon: <LockIcon /> },
];

const balances = [
  { key: 'row1', amount: 300 },
  { key: 'row2', amount: 300 },
  { key: 'row3', amount: 120 },
];

const onDark = 'rgba(255,255,255,.86)';

/** The balance card a group sees after the hero's trip conversation. */
const BalanceCard: React.FC = () => {
  const { t, i18n } = useTranslation('landing');
  return (
    <Box
      sx={{
        flex: '1 1 360px',
        minWidth: 0,
        maxWidth: 460,
        bgcolor: landing.surface,
        color: landing.ink,
        borderRadius: '28px',
        p: 3.5,
        boxShadow: '0 30px 60px -30px rgba(0,0,0,.5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.25,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box
            component='span'
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: landing.ink3,
            }}
          >
            {t('groups.card.label')}
          </Box>
          <Box
            component='span'
            sx={{
              fontFamily: landing.display,
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            {t('chat.groupName')}
          </Box>
        </Box>
        <Box sx={{ display: 'flex' }}>
          <Initial letter='B' size={32} {...people.bruna} />
          <Initial letter='D' size={32} {...people.diego} overlap />
          <Initial letter='A' size={32} {...people.ana} overlap />
          <Initial
            letter={t('groups.card.me')}
            size={32}
            color={landing.accent}
            tint={landing.accentSoft2}
            overlap
          />
        </Box>
      </Box>
      <Box component='ul' sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {balances.map((row) => (
          <Box
            key={row.key}
            component='li'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              py: 1.75,
              borderTop: `1px solid ${landing.line}`,
              fontSize: 15,
            }}
          >
            <span>{richText(t(`groups.card.${row.key}`))}</span>
            <Box
              component='span'
              sx={{
                fontFamily: landing.display,
                fontSize: 18,
                fontWeight: 700,
                color: landing.warn,
                whiteSpace: 'nowrap',
              }}
            >
              {formatBRL(row.amount, i18n.language)}
            </Box>
          </Box>
        ))}
        <Box
          component='li'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            py: 1.75,
            borderTop: `1px solid ${landing.line}`,
            borderBottom: `1px solid ${landing.line}`,
            fontSize: 15,
          }}
        >
          <strong>{t('groups.card.you')}</strong>
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 14,
              fontWeight: 700,
              color: landing.accent,
              '& svg': { fontSize: 18 },
            }}
          >
            <CheckIcon />
            {t('groups.card.settled')}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1.5,
          fontSize: 14,
          color: landing.ink3,
        }}
      >
        <span>{t('groups.card.total')}</span>
        <span>
          <Box component='strong' sx={{ color: landing.ink }}>
            {formatBRL(1440, i18n.language)}
          </Box>{' '}
          · {t('groups.card.expenses')}
        </span>
      </Box>
    </Box>
  );
};

const GroupsSection: React.FC = () => {
  const { t } = useTranslation('landing');
  const useCases = t('groups.useCases', { returnObjects: true }) as unknown as string[];
  return (
    <Section id='grupos' sx={{ bgcolor: landing.accentDeep, color: '#FFFFFF' }}>
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          <Box
            sx={{
              flex: '1 1 560px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.25,
            }}
          >
            <Box
              component='span'
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,.82)',
              }}
            >
              {t('groups.eyebrow')}
            </Box>
            <Box
              component='h2'
              sx={{
                fontSize: 'clamp(36px, 4.6vw, 60px)',
                lineHeight: 1.02,
                letterSpacing: '-0.035em',
                fontWeight: 700,
              }}
            >
              {t('groups.title')}
            </Box>
            <Box
              component='p'
              sx={{ fontSize: 18, lineHeight: 1.6, color: onDark, maxWidth: 600 }}
            >
              {t('groups.description')}
            </Box>
          </Box>
          <TelegramCta label={t('groups.cta')} tone='white' size='md' />
        </Box>

        <Box
          component='ul'
          aria-label={t('groups.useCasesLabel')}
          sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 1.25 }}
        >
          {useCases.map((useCase) => (
            <Box
              key={useCase}
              component='li'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 40,
                px: 2,
                borderRadius: '999px',
                border: '1px solid rgba(255,255,255,.28)',
                fontSize: 15,
              }}
            >
              {useCase}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'flex-start' }}>
          <Box
            sx={{
              flex: '1 1 480px',
              minWidth: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
              gap: '32px 28px',
            }}
          >
            {features.map((feature) => (
              <Box
                key={feature.key}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
              >
                <IconTile sx={{ bgcolor: 'rgba(255,255,255,.12)', color: '#FFFFFF' }}>
                  {feature.icon}
                </IconTile>
                <Box component='h3' sx={{ fontSize: 21, letterSpacing: '-0.01em' }}>
                  {t(`groups.features.${feature.key}.title`)}
                </Box>
                <Box component='p' sx={{ fontSize: 16, lineHeight: 1.6, color: onDark }}>
                  {t(`groups.features.${feature.key}.text`)}
                </Box>
              </Box>
            ))}
          </Box>
          <BalanceCard />
        </Box>
      </Container>
    </Section>
  );
};

export default GroupsSection;
